import React, { memo, useCallback, useMemo } from "react";
import colors from "app/colors";
import { Text, HStack, VStack } from "@chakra-ui/react";
import { SearchFilter, SearchFilterType } from "gql/graphql";
import UserTagGroupSelector, {
  Option,
} from "modules/selectors/user-tag-group-selector";
import { AtSignIcon } from "modules/icons";
import { useUsers } from "modules/providers/users-provider";
import { useDistributionGroups } from "modules/providers/distribution-groups-provider";

interface IProps {
  newSearchFilters: SearchFilter[];
  setNewSearchFilters: React.Dispatch<React.SetStateAction<SearchFilter[]>>;
}

const FiltersDrawerCalloutSelector = ({
  newSearchFilters,
  setNewSearchFilters,
}: IProps) => {
  const { usersById } = useUsers();
  const { distributionGroupsById } = useDistributionGroups();

  const getOptionValues = useCallback(
    (type: SearchFilterType): Option[] => {
      const filter = newSearchFilters.find((filter) => filter.type === type);
      if (!filter || !filter.objectIdListValue) return [];
      if (type === SearchFilterType.UserIdList) {
        return filter.objectIdListValue.map((id) => ({
          id,
          type: "user",
          active: usersById[id]?.active ?? false,
        }));
      }
      if (type === SearchFilterType.DistributionGroupIdList) {
        return filter.objectIdListValue.map((id) => ({
          id,
          type: "distribution-group",
          active: distributionGroupsById[id]?.active ?? false,
        }));
      }
      return [];
    },
    [distributionGroupsById, newSearchFilters, usersById],
  );

  const userDistributionGroupValues = useMemo<Option[]>(
    () => [
      ...getOptionValues(SearchFilterType.UserIdList),
      ...getOptionValues(SearchFilterType.DistributionGroupIdList),
    ],
    [getOptionValues],
  );

  const onUsersGroupSelectorChanged = useCallback(
    (options: Option[]) => {
      const users = options.filter((option) => option.type === "user");
      const distributionGroups = options.filter(
        (option) => option.type === "distribution-group",
      );

      setNewSearchFilters((prev) => {
        const _prev = [...prev].filter(
          (filter) =>
            filter.type !== SearchFilterType.UserIdList &&
            filter.type !== SearchFilterType.DistributionGroupIdList,
        );
        if (users.length > 0) {
          _prev.push({
            type: SearchFilterType.UserIdList,
            objectIdListValue: users.map((user) => user.id),
          });
        }
        if (distributionGroups.length > 0) {
          _prev.push({
            type: SearchFilterType.DistributionGroupIdList,
            objectIdListValue: distributionGroups.map(
              (distributionGroup) => distributionGroup.id,
            ),
          });
        }
        return _prev;
      });
    },
    [setNewSearchFilters],
  );

  return (
    <VStack w="full">
      <HStack w="full">
        <AtSignIcon fill={colors.blackSecondary} boxSize={5} />
        <Text
          fontFamily="Outfit"
          color={colors.blackSecondary}
          fontSize={18}
          fontWeight={600}
        >
          Callouts
        </Text>
      </HStack>
      <UserTagGroupSelector
        types={["user", "distribution-group"]}
        isMultiSelect
        onSelectedOptionsChanged={onUsersGroupSelectorChanged}
        allowInactive
        value={userDistributionGroupValues}
        placeHolder="@ Enter user or group"
      />
    </VStack>
  );
};

export default memo(FiltersDrawerCalloutSelector);
